// Chat-mode start for the matter-creator demo (fn-69.4, R1/R6).
//
// The fn-69 thesis: ONE deployed `matter-creator` agent powers two UIs,
// differentiated ONLY by the kickoff message. fn-69.3 drove it headless;
// this hook drives the SAME agent through the existing `/preview` `<Chat>`
// surface with the CONVERSATIONAL kickoff + the matter document inlined.
//
// It reuses the fn-69.2 driver's create-only sequence (`bootstrapAndKickoff`
// over the BFF — cookie auth, never CP-direct): create a matter-less
// session, wait out the fn-67 bootstrap turn, then send `buildChatKickoff`.
// On success the caller navigates to `/preview/:id`, where `<Chat>` paints
// the conversation and renders the `review_matter` confirmation card.
//
// Why not the adapter's `startSession`: it does create + first-send in one
// shot and would 409 against the still-running bootstrap turn (see
// `headless/transport.ts`). The driver separates the two calls.

import { useCallback, useMemo, useRef, useState } from 'react';

import { bffEndpoints } from '../../auth/bff-origin.js';
import {
  type HeadlessCreateInput,
  type HeadlessTransport,
  bootstrapAndKickoff,
  buildChatKickoff,
  createHeadlessTransport,
  toHeadlessClient,
} from '../../headless/index.js';
import { createVanillaTrpcClient } from '../../trpc-client.js';

export interface MatterChatStartInput {
  readonly agentId: string;
  /** The matter document text inlined into the conversational kickoff. */
  readonly document: string;
}

export type MatterChatStartStatus = 'idle' | 'starting' | 'started';

export interface UseMatterChatStart {
  readonly status: MatterChatStartStatus;
  readonly error: string | undefined;
  readonly start: (input: MatterChatStartInput) => void;
  readonly reset: () => void;
}

/**
 * Drive a chat-mode start. `onStarted(sessionId)` fires once the kickoff has
 * been sent; the caller navigates to the chat view. Transport failures land
 * in `error` (the form preserves its inputs).
 */
export function useMatterChatStart(onStarted: (sessionId: string) => void): UseMatterChatStart {
  const [status, setStatus] = useState<MatterChatStartStatus>('idle');
  const [error, setError] = useState<string | undefined>(undefined);
  // Monotonic per-start token: a superseded start (reset / re-submit) must
  // not navigate or surface an error after a newer one began.
  const genRef = useRef(0);

  const transport = useMemo<HeadlessTransport>(
    () =>
      createHeadlessTransport(
        toHeadlessClient(createVanillaTrpcClient({ trpcUrl: bffEndpoints().trpcUrl })),
      ),
    [],
  );

  const start = useCallback(
    async (input: MatterChatStartInput): Promise<void> => {
      const gen = ++genRef.current;
      setStatus('starting');
      setError(undefined);

      const kickoff = buildChatKickoff({ document: input.document });
      const create: HeadlessCreateInput = {
        agentId: input.agentId,
        metadata: { title: 'Matter chat' },
      };

      try {
        const boot = await bootstrapAndKickoff({ transport, create, kickoff });
        if (genRef.current !== gen) return;
        if (boot.outcome === 'bootstrap-unavailable') {
          setStatus('idle');
          setError(`The session wasn’t ready to start (${boot.status}). Try again.`);
          return;
        }
        setStatus('started');
        onStarted(boot.sessionId);
      } catch (err) {
        if (genRef.current !== gen) return;
        setStatus('idle');
        setError((err as Error).message ?? 'Couldn’t start the conversation — try again.');
      }
    },
    [transport, onStarted],
  );

  const reset = useCallback((): void => {
    genRef.current += 1;
    setStatus('idle');
    setError(undefined);
  }, []);

  return {
    status,
    error,
    start: (i) => void start(i),
    reset,
  };
}
